import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, AlertCircle } from 'lucide-react';

/**
 * One-click confirmation landing — reached via the redirect from
 * /api/donations/confirm-home-collection/{token}.
 */
export default function HomeCollectionConfirmed() {
  const [params] = useSearchParams();
  const status = params.get('status') || 'ok';

  const views = {
    ok: {
      icon: <Check className="w-20 h-20 text-[#00D26A] mx-auto mb-4" />,
      title: "You're in 🎉",
      body: (
        <>
          <p className="text-zinc-300 text-lg mb-3">
            We've saved your spot in the <strong className="text-[#FFD700]">Home Collection</strong> early-access list.
          </p>
          <ul className="text-zinc-400 text-sm space-y-2 max-w-md mx-auto text-left inline-block">
            <li>✓ 30 days free from launch day</li>
            <li>✓ Priority alerts on titles you've donated</li>
            <li>✓ £5/month after trial — cancel any time</li>
            <li>✓ We'll email you when launch is imminent (September 2026)</li>
          </ul>
        </>
      ),
    },
    unknown: {
      icon: <AlertCircle className="w-20 h-20 text-[#FFD700] mx-auto mb-4" />,
      title: 'Link already used',
      body: (
        <p className="text-zinc-300 text-lg">
          This confirmation link has already been activated, or may have expired.
          If you're already on the list, you're good to go — no action needed.
        </p>
      ),
    },
    invalid: {
      icon: <AlertCircle className="w-20 h-20 text-red-400 mx-auto mb-4" />,
      title: "That link doesn't look right",
      body: (
        <p className="text-zinc-300 text-lg">
          The confirmation link is malformed. If you copied it from an email,
          make sure the whole URL came through. Otherwise, donate a title again from the
          home page and we'll send a fresh one.
        </p>
      ),
    },
  };

  const v = views[status] || views.unknown;

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-4 py-24" data-testid="home-collection-confirmed-page">
      <div className="max-w-xl w-full text-center">
        {v.icon}
        <h1 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {v.title}
        </h1>
        <div className="mb-10" data-testid="home-collection-confirmed-body">
          {v.body}
        </div>
        <Link
          to="/"
          className="inline-block px-8 py-4 rounded-full bg-gradient-to-r from-[#00D26A] to-[#FFD700] text-black font-bold hover:opacity-90 shadow-[0_0_30px_rgba(0,210,106,0.25)]"
          data-testid="home-collection-back-home"
        >
          ← Back to Flipping Friends
        </Link>
      </div>
    </div>
  );
}
